/**
 * Typy + helpery dla mechanizmu plugin pattern w `c_user_accounts`.
 *
 * Backend endpoint: `GET /IRESTAPI/v1/ICATALOGS_CONTROLLER/cms/c_user_accounts/extensions`
 * (zob. web/.claude/IAPI.md sekcja P + dokumentacja developera w System Administration).
 *
 * Każde rozszerzenie (extension) to inny komponent CMS który deklaruje się
 * przez metodę `sharingContentForApi('c_user_accounts', ...)` w swoim `*.inc.php`.
 * Endpoint zwraca listę aktywnych pluginów dla bieżącej domeny + ich blocks
 * (renderowanych w main area panelu klienta przez `<BlockRenderer />`)
 * + opcjonalny `sidebar_badge` (licznik w sidebar pill).
 */

import { isIconName, type IconName } from "@/lib/icons/Icon";

/* ─── Block types — discriminated union po `block_type` ──────────────── */

export type MenuSectionData = {
  title: string;
  icon?: string;
  links: Array<{ label: string; href: string; icon?: string }>;
};

export type LinkListData = {
  title?: string;
  links: Array<{ label: string; href: string; badge?: string | null }>;
};

export type StatWidgetData = {
  label: string;
  value: number | string;
  icon?: string;
};

export type InfoCardData = {
  title: string;
  text: string;
  cta?: { label: string; href: string };
};

export type UserAccountBlockBase = {
  block_id: string;
  sort: number;
};

export type UserAccountBlock =
  | (UserAccountBlockBase & { block_type: "menu_section"; data: MenuSectionData })
  | (UserAccountBlockBase & { block_type: "link_list"; data: LinkListData })
  | (UserAccountBlockBase & { block_type: "stat_widget"; data: StatWidgetData })
  | (UserAccountBlockBase & { block_type: "info_card"; data: InfoCardData })
  /* Forward-compat fallback — backend może rolować nowe types przed FE; renderer
     handle'uje to przez warning + null, ale typy muszą uznać taki shape. */
  | (UserAccountBlockBase & { block_type: string; data: unknown });

export type UserAccountAction = {
  /** Wyświetlana etykieta linku (UI text, per current locale). */
  label: string;
  /** Phosphor icon name (np. "tray", "paper-plane-tilt"). Walidowane runtime
   *  przez `isIconName` — nieznane wartości renderują się bez ikony. */
  icon?: string;
  /** Target href — pełny URL akcji (np. `/pl/konto#extension-received-messages`).
   *  Generowany przez backend ILINK z prefix'em `extension-` w hash anchor. */
  href: string;
};

/**
 * Akcja odrzucona przez backend walidator (brak `extension-` prefix w href).
 * Renderowana w sidebar boksie TYLKO w dev mode z warning visual żeby
 * developer widział błąd integracji. W prod mode ignorowana.
 */
export type UserAccountInvalidAction = {
  /** Klucz action z `actions` map. */
  key: string;
  /** Label (lub fallback do key gdy plugin nie zwrócił). */
  label: string;
  /** Icon name (może być null). */
  icon: string | null;
  /** Aktualnie zwrócony href (bez `extension-` prefix). */
  href: string;
  /** Reason kod: `missing_extension_prefix` | `action_data_not_array`. */
  reason: string;
};

export type UserAccountExtension = {
  /** Komponent symbol (np. `c_private_messages`). Single source of truth dla
   *  mapowania React (key w sidebar nav, identyfikator w hash routing). */
  symbol: string;
  /** Wyświetlana nazwa pluginu = nazwa katalogu z drzewa CMS
   *  (`imp_cms_catalog.name`, już w bieżącym locale bo katalogi są per język).
   *  Tytuł boksu w sidebarze ma odpowiadać temu, jak administrator nazwał
   *  katalog udostępniający dane. Fallback (katalog bez nazwy) —
   *  `imp_cms_component.name`. */
  name: string;
  /** Path katalogu root (z `imp_cms_catalog.path`) — fallback href dla głównego
   *  linku pluginu gdy nie ma dedykowanej akcji. */
  root_href: string;
  id_domain_component: number;
  /** Licznik w sidebar pill. `null` = brak badge. */
  sidebar_badge: number | null;
  /** Mapa akcji (action_id -> {href}). Plugin sam decyduje co tu wstawia. */
  actions: Record<string, UserAccountAction>;
  /** Akcje odrzucone przez backend walidator (brak `extension-` prefix).
   *  Tylko dev mode w UI renderuje je z warning style. */
  invalid_actions?: UserAccountInvalidAction[];
  /** Bloki do renderowania w main area panelu (sortowane po `sort` ascending). */
  blocks: UserAccountBlock[];
};

export type UserAccountExtensionsResponse = {
  ok: true;
  data: {
    shell: { symbol: "c_user_accounts" };
    extensions: UserAccountExtension[];
  };
  meta: {
    extensions_count: number;
    polling_interval_seconds: number;
    /** Lista symboli komponentów które mają katalog z `imp_cms_catalog.sharing_methods='1'`
     *  ale brakuje metody `sharingContentForApi` w ich `*.inc.php`. AlignmentChecker
     *  renderuje dev banner z tymi symbolami żeby admin developer wiedział co dodać. */
    missing_sharing_content_for_api?: string[];
    /** Pluginy które zwróciły akcje bez `extension-{action_id}` marker'a w href.
     *  Endpoint odfiltrował te akcje (security/integrity check) — bez prefix'u
     *  shell `c_user_accounts` nie rozpoznałby URL'a jako akcji rozszerzenia. */
    invalid_action_hrefs?: Array<{ symbol: string; actions: string[] }>;
  };
};

/* ─── Fetch helper ────────────────────────────────────────────────────── */

/**
 * Pobiera listę pluginów dla bieżącego użytkownika z route handler proxy.
 *
 * Wywoływany client-side (CUserAccounts/ProfileTabs) — proxy w
 * `app/api/iiapi/user-account-extensions/route.ts` forwarduje do backend
 * REST z access_token z sesji.
 *
 * @returns Lista extensions + meta z polling interval (default 60s)
 * @throws Error gdy fetch fail / response !ok (caller handle'uje przez try/catch)
 */
export async function fetchUserAccountExtensions(): Promise<{
  extensions: UserAccountExtension[];
  pollingIntervalSeconds: number;
  missingSharingContentForApi: string[];
  invalidActionHrefs: Array<{ symbol: string; actions: string[] }>;
}> {
  const res = await fetch("/api/iiapi/user-account-extensions", {
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Fetch extensions failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as UserAccountExtensionsResponse | { ok: false; error?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error("Fetch extensions failed: invalid response shape");
  }
  return {
    extensions: body.data?.extensions ?? [],
    pollingIntervalSeconds: body.meta?.polling_interval_seconds ?? 60,
    missingSharingContentForApi: body.meta?.missing_sharing_content_for_api ?? [],
    invalidActionHrefs: body.meta?.invalid_action_hrefs ?? [],
  };
}

/* ─── Helper: zgadnij ikonę pluginu z jego bloków ──────────────────────── */

/**
 * Wybiera ikonę reprezentującą plugin w sidebar — najpierw szuka pierwszego
 * `stat_widget` z `data.icon` (najczęściej KPI z ikoną — np. envelope-simple
 * dla wiadomości), potem pierwszego `menu_section` z `data.icon`, w końcu
 * fallback `puzzle-piece` (universal "rozszerzenie" symbol).
 *
 * @param ext Extension obiekt
 * @returns Nazwa ikony z whitelist `IconName` (z fallbackiem dla unknown)
 */
export function inferExtensionIcon(ext: UserAccountExtension): IconName {
  for (const block of ext.blocks) {
    if (block.block_type === "stat_widget") {
      const icon = (block.data as StatWidgetData).icon;
      if (isIconName(icon)) return icon;
    }
  }
  for (const block of ext.blocks) {
    if (block.block_type === "menu_section") {
      const icon = (block.data as MenuSectionData).icon;
      if (isIconName(icon)) return icon;
    }
  }
  return "puzzle-piece";
}
